// Client-side voice input via the Web Speech API. Used by the mic
// icon inside the primary search bar to dictate an AI query.
//
// Why not the server-side Whisper endpoint we already use for voice
// bookmarks? That path uploads a full audio blob (100-500KB) and
// waits ~2-4s for the round-trip — fine for saving a voice note,
// too slow for "quick search". The browser's built-in SpeechRecognition
// (Chrome/Edge/Safari 15+) gives us live partial transcripts with
// zero server cost and sub-second latency. On unsupported browsers
// (Firefox on desktop) `isSupported()` returns false so the caller
// can hide the mic button entirely.
//
// The hook returns a stable API: {isSupported, listening, transcript,
// start, stop}. Consumers wire `start` to a mic button and read the
// running `transcript`; when `listening` flips back to false the
// transcript is final.

import { useCallback, useEffect, useRef, useState } from 'react';

// Grab the vendor-prefixed constructor once at module load. Safari
// still exposes the old webkit-prefixed name; Chrome exposes both.
const SR = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

export function useVoiceInput({ lang } = {}) {
    const [listening, setListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const recognitionRef = useRef(null);

    const isSupported = !!SR;

    const start = useCallback(() => {
        if (!isSupported || listening) return;
        try {
            const rec = new SR();
            // Continuous=false: stop after the first sustained pause,
            // which matches a "say a search query" gesture. Interim
            // results keep the transcript flowing so the search input
            // updates as the user speaks.
            rec.continuous = false;
            rec.interimResults = true;
            rec.maxAlternatives = 1;
            if (lang) rec.lang = lang;
            rec.onstart = () => { setListening(true); setTranscript(''); };
            rec.onresult = (event) => {
                let text = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    text += event.results[i][0].transcript;
                }
                setTranscript(text);
            };
            rec.onerror = () => { setListening(false); };
            rec.onend = () => { setListening(false); };
            recognitionRef.current = rec;
            rec.start();
        } catch (_) {
            setListening(false);
        }
    }, [isSupported, listening, lang]);

    const stop = useCallback(() => {
        try { recognitionRef.current?.stop(); } catch (_) { /* ignore */ }
        setListening(false);
    }, []);

    // Safety net: if the component unmounts mid-listen, kill the
    // native recognizer so it doesn't keep the mic open indefinitely.
    useEffect(() => () => { try { recognitionRef.current?.abort(); } catch (_) { /* ignore */ } }, []);

    return { isSupported, listening, transcript, start, stop };
}
